import { type i18n, type Resource } from 'i18next';

/**
 * Safely update a module-level i18n singleton for SSR without calling init()
 * on every render.
 *
 * Calling `i18n.init()` repeatedly on the same instance accumulates internal
 * event listeners, stores, and plugin state — leading to memory growth over
 * thousands of SSR requests. This helper uses the idempotent i18next APIs
 * (`changeLanguage` + `addResourceBundle`) which replace state in-place
 * without accumulation.
 *
 * The singleton is initialised exactly once (first call); subsequent calls
 * only update the language and resource bundles.
 */
let initialised = new WeakSet<i18n>();

export function updateI18nSingleton(instance: i18n, lng: string, resources: Resource): void {
  if (!initialised.has(instance)) {
    instance.init({
      lng,
      fallbackLng: 'en-US',
      defaultNS: 'label',
      resources,
      initImmediate: false,
    });
    initialised.add(instance);
    return;
  }

  // Switch language if it changed (synchronous, no accumulation)
  if (instance.language !== lng) {
    instance.changeLanguage(lng);
  }

  // Replace resource bundles for each language/namespace combination.
  // addResourceBundle with deep=false, overwrite=true is an atomic
  // in-place replacement — no accumulation of old keys.
  for (const [language, namespaces] of Object.entries(resources)) {
    if (!namespaces) continue;
    for (const [ns, bundle] of Object.entries(namespaces)) {
      instance.addResourceBundle(language, ns, bundle, false, true);
    }
  }
}
